import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Building2, Filter, Edit } from 'lucide-react';
import StatusChip from '../components/StatusChip';
import EditFilingDialog from '../components/EditFilingDialog';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';

const FilingsRegister = ({ user }) => {
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('state');
  const [loading, setLoading] = useState(true);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    loadRegistrations();
    
    // Refresh data when window/tab regains focus
    const handleFocus = () => {
      loadRegistrations();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stateFilter, sortBy, registrations]);

  const loadRegistrations = async () => {
    try {
      // SINGLE SOURCE OF TRUTH: Load from state-compliance endpoint
      const complianceData = await api.getStateCompliance();
      
      // Extract all registrations from all states (backend returns DBAs + Filings combined)
      const allRegistrations = [];
      Object.entries(complianceData.states || {}).forEach(([stateCode, stateData]) => {
        const stateRegistrations = stateData.registrations || [];
        allRegistrations.push(...stateRegistrations);
      });
      
      // Filter to show ONLY Ancillary Registrations (Filings + Foreign Corporations)
      // INCLUDE:
      //   - Filings: filing_id, filing_type, due_date
      //   - Foreign Corporations: dba_id with registration_type='Foreign Corporation'
      // EXCLUDE:
      //   - Regular DBAs: registration_type='DBA'
      const allFilings = allRegistrations.filter(reg => {
        // Exclude regular DBAs
        if (reg.registration_type === 'DBA') {
          return false;
        }
        
        // Include filings (have filing_id or filing_type)
        if (reg.filing_id || reg.filing_type) {
          return true;
        }
        
        // Include Foreign Corporations and other non-DBA registrations
        if (reg.registration_type && reg.registration_type !== 'DBA') {
          return true;
        }
        
        return false;
      });
      
      // Sort by state and name (filing_type or dba_name)
      allFilings.sort((a, b) => {
        if (a.state !== b.state) return a.state.localeCompare(b.state);
        const nameA = a.filing_type || a.dba_name || '';
        const nameB = b.filing_type || b.dba_name || '';
        return nameA.localeCompare(nameB);
      });
      
      setRegistrations(allFilings);
      setFilteredRegistrations(allFilings);
    } catch (error) {
      console.error('Failed to load registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...registrations];
    
    // Apply state filter
    if (stateFilter !== 'all') {
      filtered = filtered.filter(r => r.state === stateFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'state') {
        return a.state.localeCompare(b.state);
      } else if (sortBy === 'filing_type') {
        const typeA = a.filing_type || a.dba_name || '';
        const typeB = b.filing_type || b.dba_name || '';
        return typeA.localeCompare(typeB);
      } else if (sortBy === 'registration_type') {
        const regTypeA = a.registration_type || 'Filing';
        const regTypeB = b.registration_type || 'Filing';
        return regTypeA.localeCompare(regTypeB);
      } else if (sortBy === 'expiration_date') {
        const dateA = a.expiration_date ? new Date(a.expiration_date) : new Date('9999-12-31');
        const dateB = b.expiration_date ? new Date(b.expiration_date) : new Date('9999-12-31');
        return dateA - dateB;
      }
      return 0;
    });
    
    setFilteredRegistrations(filtered);
  };

  const handleEdit = (registration) => {
    setEditingRegistration(registration);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    loadRegistrations();
  };

  const states = [...new Set(registrations.map(r => r.state))].sort();

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="ancillary-registrations-container">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">Ancillary Registrations</h1>
        <p className="text-muted-foreground mt-2">Foreign qualifications and other state registrations</p>
      </div>

      <Card data-testid="filter-bar">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            
            {/* State Filter */}
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="state">Sort by State</SelectItem>
                <SelectItem value="filing_type">Sort by Type</SelectItem>
                <SelectItem value="registration_type">Sort by Category</SelectItem>
                <SelectItem value="expiration_date">Sort by Expiration</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="text-sm text-muted-foreground sm:ml-auto">
              Showing {filteredRegistrations.length} of {registrations.length} registrations
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto" data-testid="registrations-table">
        <table className="w-full min-w-[900px]">
          <thead className="table-header">
            <tr className="border-b border-border">
              <th className="table-cell text-left font-medium text-muted-foreground">Registration ID</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Name</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Type</th>
              <th className="table-cell text-left font-medium text-muted-foreground">State</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Registration Date</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Expiration</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Days Until</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Status</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRegistrations.map((registration) => {
              const daysUntil = registration.expiration_date ? getDaysUntil(registration.expiration_date) : null;
              
              // Handle both Filing and Foreign Corporation records
              const recordId = registration.filing_id || registration.dba_id;
              const recordName = registration.filing_type || registration.dba_name;
              const recordDate = registration.filing_date || registration.registration_date;
              const recordType = registration.registration_type || 'Filing';
              
              return (
                <tr key={recordId} className="table-row border-b border-border" data-testid={`registration-row-${recordId}`}>
                  <td className="table-cell table-cell-mono">{recordId}</td>
                  <td className="table-cell font-medium">{recordName}</td>
                  <td className="table-cell">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                      {recordType}
                    </span>
                  </td>
                  <td className="table-cell">{registration.state}</td>
                  <td className="table-cell tabular-nums">{formatDate(recordDate)}</td>
                  <td className="table-cell tabular-nums">{formatDate(registration.expiration_date)}</td>
                  <td className="table-cell">
                    {daysUntil !== null ? (
                      <span className={`font-medium ${
                        daysUntil < 0 ? 'text-rose-600' : 
                        daysUntil <= 60 ? 'text-amber-600' : 
                        'text-muted-foreground'
                      }`}>
                        {daysUntil < 0 ? `${Math.abs(daysUntil)} days ago` : `${daysUntil} days`}
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td className="table-cell">
                    <StatusChip status={registration.status} />
                  </td>
                  <td className="table-cell">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(registration)}
                      data-testid={`edit-registration-${recordId}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredRegistrations.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No registrations match the current filters
          </div>
        )}
      </div>
      
      {/* Edit Filing Dialog */}
      <EditFilingDialog
        filing={editingRegistration}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingRegistration(null);
        }}
        onSuccess={() => {
          setEditDialogOpen(false);
          setEditingRegistration(null);
          handleEditSuccess();
        }}
      />
    </div>
  );
};

export default FilingsRegister;
